import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, switchMap, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import { AuthService } from './auth.service';
import { User } from '../models/user';
import { NimmstaCommonsModuleConfiguration } from '../nimmsta-commons-module.config';
import { PrimeNG } from 'primeng/config';
import type { Translation as PrimeNGTranslation } from 'primeng/api';
import Keycloak from 'keycloak-js';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  LANGUAGE_KEY = 'language';

  languageChange: Subject<string> = new Subject<string>();

  user: User | null = null;

  private allowedLanguages = ['en', 'de'];

  private configuration = inject(NimmstaCommonsModuleConfiguration);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  private primengConfig = inject(PrimeNG);
  private keycloak = inject(Keycloak);

  constructor() {
    this.translate.setFallbackLang('en');
    this.authService.getCurrentUser().subscribe((user) => {
      this.user = user;
    });
  }

  initializeLanguage(
    language = this.getPreferredLanguage(),
  ): Observable<unknown> {
    language = this.resolveLanguage(language);
    dayjs.locale(language);

    return this.translate.use(language).pipe(
      switchMap(() => this.translate.get('primeng')),
      tap((translation) => {
        this.primengConfig.setTranslation(translation as PrimeNGTranslation);
        this.languageChange.next(language);
      }),
    );
  }

  changeLanguage(language: string, savePreferred = false): void {
    language = this.resolveLanguage(language);

    if (savePreferred && this.user) {
      // Manipulate the User Language by using the Keycloak Account API
      // (User self-service, unducumented, built-in Keycloak feature)
      // See https://auth.dev.nimmsta-test.de/realms/productivity-cloud/account/

      // https://github.com/keycloak/keycloak/blob/main/services/src/main/java/org/keycloak/services/resources/account/AccountRestService.java#L153
      // Authentification is done by the Authorization header with the access token
      // of the current user, will only modify the current user
      this.http
        .post<void>(
          `${this.configuration.keycloakUrl}/realms/${this.configuration.keycloakRealm}/account`,
          {
            attributes: {
              locale: [language],
            },
            firstName: this.user.given_name, // Required, validation error if not set
            lastName: this.user.family_name, // Required, validation error if not set
          },
        )
        .subscribe({
          next: this.applyLanguage.bind(this, language),
          error: () => {
            this.applyLanguage(language);
          },
        });
    } else {
      this.applyLanguage(language);
    }
  }

  private applyLanguage(language: string): void {
    this.initializeLanguage(language).subscribe();
  }

  getPreferredLanguage(): string {
    return this.resolveLanguage(
      (this.keycloak.authenticated &&
        this.keycloak.idTokenParsed?.['locale']) ||
        this.translate.getBrowserLang() ||
        'en',
    );
  }

  private resolveLanguage(language: unknown): string {
    const baseLanguage =
      typeof language === 'string'
        ? language.trim().toLowerCase().split(/[-_]/)[0]
        : '';
    return this.allowedLanguages.includes(baseLanguage) ? baseLanguage : 'en';
  }
}
